import React, { useState } from "react";
import debug from "debug";
import { ClickAwayListener, Input, InputAdornment, Popper } from "@material-ui/core";
import { Search } from "@material-ui/icons";
import { arrayToTree, filterTreeDataByString, TreeItem } from "@bonliva-crm/common";
import { hasClassInParent } from "@bonliva-crm/ui";
import { MenuRendererProps, Option } from "../utils";

const log = debug("app:Select:NestedMenuRenderer");

type Props = MenuRendererProps & {
  isOpen: boolean;
  onClose: () => void;
};

export function NestedMenuRenderer(props: Props) {
  const [searchValue, setSearchValue] = useState("");

  const anchor = document.getElementById(`${props.instancePrefix}-value`).parentElement
    .parentElement;

  const anchorWidth = anchor.getBoundingClientRect().width;

  const OptionCmp = props.optionComponent;

  const tree = arrayToTree(
    props.options.map((x) => ({ ...x, id: x.value, parentId: x.parent })),
    {
      id: (x) => x.value,
      parentId: (x) => x.parent
    }
  );

  function renderNode(node: TreeItem<Option>, index, filter, level = 0) {
    return (
      <React.Fragment key={`${index}-${level}`}>
        <OptionCmp
          key={`option-${index}-${node.value}`}
          focusOption={props.focusOption}
          inputValue={props.inputValue || searchValue}
          instancePrefix={props.instancePrefix}
          isDisabled={node.value.disabled}
          isFocused={
            props.focusedOption && node.value.value === props.focusedOption.value
          }
          isSelected={!!props.valueArray.find((x) => x.value === node.value.value)}
          onFocus={props.onFocus}
          onSelect={props.onSelect}
          parentLevel={level}
          option={node.value}
          removeValue={props.removeValue}
          selectValue={props.selectValue}
        >
          {node.value.label}
        </OptionCmp>

        {(node.children || []).map((x, i) => renderNode(x, i, filter, level + 1))}
      </React.Fragment>
    );
  }

  const filteredTree = filterTreeDataByString<Option>(
    tree,
    searchValue,
    (x) => x.label as string
  );
  const items = filteredTree.map((option, i) => renderNode(option, i, searchValue, 0));

  return (
    <Popper
      open
      id={props.instancePrefix}
      anchorEl={anchor}
      style={{
        zIndex: 9999
      }}
      disablePortal
      placement="bottom-start"
    >
      <ClickAwayListener
        onClickAway={(e) => {
          log("onClickAway", e);

          if (hasClassInParent(e.target, "Select-control")) {
            return;
          }

          props.onClose();
        }}
      >
        <div className="Select-menu-dropdown" style={{ minWidth: anchorWidth }}>
          <div style={{ padding: "16px 24px" }}>
            <Input
              autoFocus
              fullWidth
              startAdornment={
                <InputAdornment position="start">
                  <Search fontSize="small" color="action" />
                </InputAdornment>
              }
              inputProps={{
                onFocus: (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              value={searchValue}
              onChange={({ target: { value } }) => setSearchValue(value)}
            />
          </div>
          {items}
        </div>
      </ClickAwayListener>
    </Popper>
  );
}
